import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtEUR, fmtDate, formatApiError } from "../lib/api";
import { Plus, Search, Trash2, FileCheck2, FileDown } from "lucide-react";
import { EmailStatusBadge } from "../components/EmailDialog";

const STATUS = {
  entwurf: { label: "Entwurf", cls: "bg-zinc-100 text-zinc-700 border-zinc-200" },
  gesendet: { label: "Gesendet", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  angenommen: { label: "Angenommen", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  abgelehnt: { label: "Abgelehnt", cls: "bg-red-50 text-red-700 border-red-200" },
};

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("alle");
  const [abFilter, setAbFilter] = useState("alle");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");

  const refresh = () => {
    setLoading(true);
    api.get("/quotes").then((r) => { setQuotes(r.data); setLoading(false); });
  };
  useEffect(() => { refresh(); }, []);

  const onDelete = async (q) => {
    if (!confirm(`Angebot ${q.quoteNumber} wirklich löschen?`)) return;
    await api.delete(`/quotes/${q.id}`);
    refresh();
  };

  const downloadBlob = (data, filename) => {
    const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const createConfirmation = async (q) => {
    setBusyId(q.id); setError("");
    try {
      const c = await api.post(`/quotes/${q.id}/confirmation`);
      const res = await api.get(`/quotes/${q.id}/confirmation/pdf`, { responseType: "blob" });
      downloadBlob(res.data, `${c.data.number}.pdf`);
      refresh();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const downloadConfirmation = async (q) => {
    setBusyId(q.id); setError("");
    try {
      const res = await api.get(`/quotes/${q.id}/confirmation/pdf`, { responseType: "blob" });
      downloadBlob(res.data, `${q.confirmation.number}.pdf`);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const filtered = quotes.filter((q) => {
    if (statusFilter !== "alle" && q.status !== statusFilter) return false;
    if (abFilter === "mit_ab" && !q.confirmation) return false;
    if (abFilter === "ohne_ab" && q.confirmation) return false;
    if (!filter) return true;
    const t = filter.toLowerCase();
    return [q.quoteNumber, q.confirmation?.number, q.customer?.name, q.customer?.company, q.project?.name]
      .filter(Boolean).some((v) => v.toLowerCase().includes(t));
  });

  return (
    <div className="p-6 md:p-10 max-w-7xl" data-testid="quotes-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 md:mb-8 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Angebote</div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-zinc-900">Alle Angebote</h1>
        </div>
        <Link to="/quote/new" className="bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium px-4 py-2.5 rounded-sm flex items-center justify-center gap-2">
          <Plus size={15} /> Neues Angebot
        </Link>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            placeholder="Suche..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="quotes-search-input"
            className="w-full pl-9 pr-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm focus:border-black focus:ring-1 focus:ring-black"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="quotes-status-filter"
          className="px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm"
        >
          <option value="alle">Alle Status</option>
          <option value="entwurf">Entwurf</option>
          <option value="gesendet">Gesendet</option>
          <option value="angenommen">Angenommen</option>
          <option value="abgelehnt">Abgelehnt</option>
        </select>
        <select
          value={abFilter}
          onChange={(e) => setAbFilter(e.target.value)}
          data-testid="quotes-ab-filter"
          className="px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm"
        >
          <option value="alle">AB/Proforma: Alle</option>
          <option value="mit_ab">Mit AB/Proforma</option>
          <option value="ohne_ab">Ohne AB/Proforma</option>
        </select>
      </div>

      {error && (
        <div className="text-xs font-medium text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-sm mb-4" data-testid="quotes-error">{error}</div>
      )}

      <div className="border border-zinc-200 overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center text-sm text-zinc-500">Wird geladen...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-zinc-500" data-testid="quotes-empty-state">
            Keine Angebote gefunden.
          </div>
        ) : (
          <table className="w-full text-sm min-w-[640px]">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200 bg-zinc-50">
              <tr>
                <th className="text-left px-5 py-2.5 font-medium">Nummer</th>
                <th className="text-left px-5 py-2.5 font-medium">Kunde</th>
                <th className="text-left px-5 py-2.5 font-medium hidden md:table-cell">Projekt</th>
                <th className="text-left px-5 py-2.5 font-medium hidden lg:table-cell">Ersteller</th>
                <th className="text-left px-5 py-2.5 font-medium hidden sm:table-cell">Datum</th>
                <th className="text-left px-5 py-2.5 font-medium">Status</th>
                <th className="text-left px-5 py-2.5 font-medium hidden md:table-cell">E-Mail</th>
                <th className="text-left px-5 py-2.5 font-medium">AB/Proforma</th>
                <th className="text-right px-5 py-2.5 font-medium">Gesamt</th>
                <th className="text-right px-5 py-2.5 font-medium w-12"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((q) => {
                const st = STATUS[q.status] || STATUS.entwurf;
                return (
                  <tr key={q.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-5 py-3 font-mono text-xs">
                      <Link to={`/quote/${q.id}`} className="text-zinc-900 hover:underline">
                        {q.quoteNumber}
                      </Link>
                    </td>
                    <td className="px-5 py-3 text-zinc-900">
                      {q.customer?.company || q.customer?.name || "—"}
                    </td>
                    <td className="px-5 py-3 text-zinc-600 hidden md:table-cell">{q.project?.name || "—"}</td>
                    <td className="px-5 py-3 text-zinc-600 text-xs hidden lg:table-cell">{q.createdByName || "—"}</td>
                    <td className="px-5 py-3 text-zinc-600 font-mono text-xs hidden sm:table-cell">{fmtDate(q.createdAt)}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 border ${st.cls}`}>
                        {st.label}
                      </span>
                    </td>
                    <td className="px-5 py-3 hidden md:table-cell">
                      {q.emails?.length ? (
                        <div className="flex flex-col gap-1 items-start">
                          <EmailStatusBadge status={q.emails[q.emails.length - 1].status} testId={`email-badge-${q.quoteNumber}`} />
                          {q.emails.some((e) => e.docType === "reminder") && (
                            <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200" data-testid={`reminder-badge-${q.quoteNumber}`}>Erinnerung gesendet</span>
                          )}
                        </div>
                      ) : <span className="text-zinc-300">—</span>}
                    </td>
                    <td className="px-5 py-3">
                      {q.confirmation ? (
                        <button
                          onClick={() => downloadConfirmation(q)}
                          disabled={busyId === q.id}
                          data-testid={`download-confirmation-${q.quoteNumber}`}
                          title={`${q.confirmation.number} herunterladen`}
                          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2 py-0.5 border bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 disabled:opacity-50"
                        >
                          <FileCheck2 size={11}/> {q.confirmation.number}
                          <FileDown size={11}/>
                        </button>
                      ) : (
                        <button
                          onClick={() => createConfirmation(q)}
                          disabled={busyId === q.id}
                          data-testid={`create-confirmation-${q.quoteNumber}`}
                          title="Auftragsbestätigung / Proforma-Rechnung erstellen"
                          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2 py-0.5 border border-zinc-300 text-zinc-600 hover:bg-zinc-100 disabled:opacity-50"
                        >
                          <FileCheck2 size={11}/> Erstellen
                        </button>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right font-mono">{fmtEUR(q.totals?.total)}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => onDelete(q)} className="text-zinc-400 hover:text-red-600" data-testid={`delete-quote-${q.quoteNumber}`}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
